import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';

const CollegeLeaderboard = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [years, setYears] = useState([]);
  const [courses, setCourses] = useState([]);
  const [filters, setFilters] = useState({
    year: 'all',
    course: 'all',
    sort: 'aptitude',
    page: 1
  });
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    if (user?.college?.college_id) {
      fetchLeaderboard();
    } else if (user && !user.college?.college_id) {
      setLoading(false);
    }
  }, [filters, user]);

  const fetchFilters = async () => {
    try {
      const yearRes = await api.get('/leaderboard/years');
      setYears(yearRes.data.years);
      
      const courseRes = await api.get('/leaderboard/courses');
      setCourses(courseRes.data.courses);
    } catch (error) {
      console.error('Failed to fetch filters:', error);
    }
  };

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await api.get('/leaderboard', {
        params: {
          type: 'college',
          college_id: user.college?.college_id,
          year: filters.year,
          course: filters.course,
          sort: filters.sort,
          page: filters.page,
          limit: 50
        }
      });
      setLeaderboard(response.data.leaderboard);
      setMeta(response.data.meta);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBadgeStyle = (score) => {
    if (score >= 81) return 'bg-yellow-100 text-yellow-700 border border-yellow-300';
    if (score >= 61) return 'bg-blue-100 text-blue-700 border border-blue-300';
    if (score >= 41) return 'bg-green-100 text-green-700 border border-green-300';
    if (score >= 21) return 'bg-orange-100 text-orange-700 border border-orange-300';
    return 'bg-gray-100 text-gray-700 border border-gray-300';
  };

  const getBadgeIcon = (score) => {
    if (score >= 81) return 'bx-crown';
    if (score >= 61) return 'bx-medal';
    if (score >= 41) return 'bx-trophy';
    if (score >= 21) return 'bx-star';
    return 'bx-rocket';
  };

  const getBadgeLabel = (score) => {
    if (score >= 81) return 'Level 5';
    if (score >= 61) return 'Level 4';
    if (score >= 41) return 'Level 3';
    if (score >= 21) return 'Level 2';
    return 'Level 1';
  };

  const getStudentSkillAverage = (student) => {
    const aptitude = student?.skill_scores?.aptitude || 0;
    const verbal = student?.skill_scores?.verbal || 0;
    const problemSolving = student?.skill_scores?.problem_solving || 0;

    return Math.round((aptitude + verbal + problemSolving) / 3);
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const sortOptions = [
    { value: 'aptitude', label: 'Aptitude Score' },
    { value: 'verbal', label: 'Verbal Score' },
    { value: 'problem_solving', label: 'Problem Solving Score' }
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
          <i className='bx bx-buildings text-blue-600'></i>
          <span>College Leaderboard</span>
        </h1>
        <div className="flex items-center space-x-2 text-gray-600 mt-1">
          <i className='bx bxs-school text-sm'></i>
          <p>{user?.college?.name}</p>
        </div>
        <p className="text-sm text-gray-500 mt-1">Compare yourself with students in your college</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <div className="flex items-center space-x-2 mb-4">
          <i className='bx bx-filter text-lg text-gray-700'></i>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Filters</h3>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="w-40">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Graduation Year
            </label>
            <select
              value={filters.year}
              onChange={(e) => handleFilterChange('year', e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Years</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="w-40">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Course
            </label>
            <select
              value={filters.course}
              onChange={(e) => handleFilterChange('course', e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Courses</option>
              {courses.map((course) => (
                <option key={course} value={course}>
                  {course}
                </option>
              ))}
            </select>
          </div>

          <div className="w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Sort By
            </label>
            <select
              value={filters.sort}
              onChange={(e) => handleFilterChange('sort', e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Leaderboard Table */}
      {loading ? (
        <div className="text-center py-12">Loading leaderboard...</div>
      ) : (
        <>
          {/* User Rank Card */}
          {user && (
            <div className="mb-4 bg-purple-50 border border-purple-200 rounded-lg shadow-sm overflow-hidden p-3 md:p-4">
               <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                 <div className="flex items-center space-x-3 md:space-x-6">
                     <div className="text-center min-w-[50px]">
                         <div className="text-[10px] text-purple-600 font-bold uppercase tracking-wider mb-0.5">Your Rank</div>
                         <div className="text-xl md:text-2xl font-bold text-purple-800">{meta?.userRank ? `#${meta.userRank}` : 'N/A'}</div>
                     </div>
                     <div className="h-8 md:h-10 w-px bg-purple-200"></div>
                     <div className="flex items-center">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center mr-3 border-2 border-purple-200 overflow-hidden shadow-sm">
                          {user.profile_photo ? (
                            <img 
                              src={user.profile_photo.startsWith('http') ? user.profile_photo : `${process.env.REACT_APP_IMG_URL || 'http://localhost:5000'}${user.profile_photo}`} 
                              alt="Profile" 
                              className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover" 
                            />
                          ) : (
                            <i className='bx bx-user text-xl text-gray-400'></i>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                             <div className="text-sm md:text-base font-bold text-gray-900">{user.name}</div>
                             <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold shadow-sm flex items-center ${getBadgeStyle(getStudentSkillAverage(user))}`}>
                               <i className={`bx ${getBadgeIcon(getStudentSkillAverage(user))} mr-0.5`}></i>
                               {getBadgeLabel(getStudentSkillAverage(user))}
                             </span>
                          </div>
                          <div className="text-xs text-gray-600 flex items-center mt-0.5">
                             <i className='bx bxs-school mr-1'></i>
                             <span className="truncate max-w-[150px] md:max-w-xs">{user.college?.name}</span>
                          </div>
                        </div>
                     </div>
                 </div>
                 
                 <div className="flex items-center justify-between md:justify-end space-x-2 md:space-x-6 w-full md:w-auto bg-purple-100/50 md:bg-transparent rounded-lg p-2 md:p-0">
                     <div className="text-center px-2">
                        <div className="text-[10px] text-gray-500 font-semibold mb-0.5">Aptitude</div>
                        <div className="font-bold text-blue-600 text-lg md:text-xl">{user.skill_scores?.aptitude || 0}</div>
                     </div>
                     <div className="h-6 w-px bg-gray-300 md:hidden"></div>
                     <div className="text-center px-1">
                        <span className="text-[10px] text-gray-500 block font-medium mb-0.5">Verbal</span>
                        <span className="font-bold text-gray-800 text-sm md:text-base">{user.skill_scores?.verbal || 0}</span>
                     </div>
                     <div className="text-center px-1">
                        <span className="text-[10px] text-gray-500 block font-medium mb-0.5">Problem Solving</span>
                        <span className="font-bold text-gray-800 text-sm md:text-base">{user.skill_scores?.problem_solving || 0}</span>
                     </div>
                 </div>
               </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Year
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Aptitude
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Verbal
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Problem Solving
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leaderboard.map((student) => (
                    <tr key={student.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        {student.rank <= 3 ? (
                          <span className="text-xl mr-1">
                            {student.rank === 1 ? '🥇' : student.rank === 2 ? '🥈' : '🥉'}
                          </span>
                        ) : null}
                        <span className="text-sm font-bold text-gray-900">#{student.rank}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center mr-3 overflow-hidden">
                          {student.profile_photo ? (
                            <img 
                              src={student.profile_photo.startsWith('http') ? student.profile_photo : `${process.env.REACT_APP_IMG_URL || 'http://localhost:5000'}${student.profile_photo}`} 
                              alt="Profile" 
                              className="w-10 h-10 rounded-full object-cover" 
                            />
                          ) : (
                            <i className='bx bx-user text-xl text-gray-500'></i>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <div className="text-sm font-medium text-gray-900 w-40 truncate" title={student.name}>{student.name}</div>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold shadow-sm flex items-center ${getBadgeStyle(getStudentSkillAverage(student))}`}>
                              <i className={`bx ${getBadgeIcon(getStudentSkillAverage(student))} mr-0.5`}></i>
                              {getBadgeLabel(getStudentSkillAverage(student))}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">{student.course}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                      {student.graduation_year}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className="text-sm font-bold text-blue-600">
                        {student.skill_scores?.aptitude ?? 0}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                      {student.skill_scores?.verbal ?? 0}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                      {student.skill_scores?.problem_solving ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {/* Pagination */}
          {meta && meta.total_pages > 1 && (
            <div className="mt-6 flex justify-center space-x-2">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={filters.page === 1}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-4 py-2">
                Page {meta.page} of {meta.total_pages}
              </span>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={filters.page === meta.total_pages}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CollegeLeaderboard;
